/**
 * arbitre401.utils.ts
 * ----------------------------------------------------------------------------
 * Arbitre des 401 : « session morte » ou « simple refus d'accès » ?
 *
 * POURQUOI : l'intercepteur HTTP déconnectait sur TOUT 401. Or LoopBack répond
 * 401 aussi bien pour un jeton mort que pour un refus d'ACL sur une session
 * parfaitement valide — un client ouvrant un écran qui déclenche un appel
 * interdit à son profil se retrouvait éjecté de l'application (incident
 * ULTRAnet du 31/07/2026, contourné à l'époque en masquant les appels).
 *
 * L'arbitre tranche en interrogeant une VÉRIFICATION DE SESSION fournie par
 * l'appelant (un appel léger accessible à tout profil authentifié) :
 *   - elle répond « vivant »  → verdict `conserver` : la session reste, le 401
 *     n'était qu'un refus d'accès ;
 *   - elle répond « mort » ou échoue → verdict `deconnecter` : comportement
 *     historique.
 *
 * DÉBOUNCE : une rafale de 401 simultanés (pollers, chargements parallèles) ne
 * déclenche qu'UNE vérification — tous les signalements du cycle partagent le
 * même verdict, et un seul est marqué `premier` pour porter les effets de bord
 * (toast, purge de session). Un 401 postérieur au cycle relance une
 * vérification neuve : un verdict ne vaut que pour sa rafale.
 *
 * Classe PURE (aucune dépendance Angular) : testée par arbitre401.utils.spec.ts.
 * ----------------------------------------------------------------------------
 */

export type Verdict401 = 'conserver' | 'deconnecter';

export interface Resultat401 {
  verdict: Verdict401;
  /** Vrai pour le SEUL signalement qui a ouvert le cycle : à lui les effets de bord. */
  premier: boolean;
}

export class Arbitre401 {

  /** Cycle en cours : partagé par tous les 401 arrivés avant le verdict. */
  private cycle: Promise<Verdict401> | null = null;

  /**
   * @param verifierSession résout `true` si la session est vivante. Tout échec
   *                        (réseau, 401 sur la vérification) vaut « morte ».
   */
  constructor(private readonly verifierSession: () => Promise<boolean>) {}

  signaler401(): Promise<Resultat401> {
    const premier = this.cycle === null;

    if (premier) {
      const cycleCourant = this.verifierSession()
        .then((vivante): Verdict401 => (vivante ? 'conserver' : 'deconnecter'))
        .catch((): Verdict401 => 'deconnecter');
      this.cycle = cycleCourant;
      // Clôture du cycle au verdict : les signalements en vol gardent leur
      // référence, les suivants re-vérifieront — la session peut mourir entre
      // deux rafales.
      cycleCourant.then(() => { if (this.cycle === cycleCourant) { this.cycle = null; } });
    }

    return this.cycle!.then((verdict) => ({ verdict, premier }));
  }
}
